import { Link } from "react-router-dom";
import { ArrowRightOutlined } from "@ant-design/icons";
import { categoryEntries, categoryHref } from "@/data/category-tree.js";
import { CDN_URL } from "@/theme.js";
import { LatestTextures } from "./LatestTextures.js";

export function Home() {
  const categories = categoryEntries.filter(([, c]) => !c.child);
  const categoryCount = categories.length;

  return (
    <div>
      <section
        aria-label="About"
        style={{
          padding: "8px 0 20px",
          borderBottom: "1px solid var(--tn-border-faint)",
          marginBottom: 24,
          display: "flex",
          flexDirection: "column",
          gap: 10,
        }}
      >
        <span className="tn-eyebrow">Public domain &middot; CC0</span>
        <h1
          style={{
            margin: 0,
            fontSize: 30,
            lineHeight: 1.15,
            fontWeight: 600,
            letterSpacing: "-0.015em",
          }}
        >
          Royalty-free Textures
        </h1>
        <p
          style={{
            margin: 0,
            maxWidth: 680,
            color: "rgba(255,255,255,0.6)",
            fontSize: 15,
            lineHeight: 1.55,
          }}
        >
          Ready to download and use for art, games, and 3D work. No account, no attribution
          required.
        </p>
      </section>

      <section aria-label="Categories" style={{ marginBottom: 44 }}>
        <div className="tn-section-header">
          <h2>Browse by category</h2>
          <span className="tn-section-sub">{categoryCount} categories</span>
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
            gap: 14,
          }}
        >
          {categories.map(([, c]) => (
            <CategoryCard
              key={c.id}
              href={categoryHref({ id: c.id, category: c.category, children: c.children })}
              thumb={c.thumb}
              name={c.category}
              count={Number(c.count)}
            />
          ))}
        </div>
      </section>

      <section>
        <LatestTextures />
      </section>
    </div>
  );
}

interface CategoryCardProps {
  href: string;
  thumb: string;
  name: string;
  count: number;
}

function CategoryCard({ href, thumb, name, count }: CategoryCardProps) {
  return (
    <Link
      to={href}
      className="tn-cat-card"
      style={{
        position: "relative",
        display: "block",
        textDecoration: "none",
        color: "#fff",
        borderRadius: 12,
        overflow: "hidden",
        background: "var(--tn-card)",
        border: "1px solid rgba(255,255,255,0.06)",
        aspectRatio: "1 / 1",
        transition: "transform 220ms ease, border-color 220ms ease, box-shadow 220ms ease",
      }}
    >
      <img
        alt={name}
        src={CDN_URL + thumb}
        loading="lazy"
        className="tn-cat-card__img"
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          transition: "transform 400ms ease, filter 220ms ease",
        }}
      />
      <div
        className="tn-cat-card__scrim"
        style={{
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(180deg, rgba(0,0,0,0) 35%, rgba(0,0,0,0.45) 70%, rgba(0,0,0,0.88) 100%)",
          pointerEvents: "none",
          transition: "opacity 220ms ease",
        }}
      />
      <div
        className="tn-cat-card__caption"
        style={{
          position: "absolute",
          left: 12,
          right: 12,
          bottom: 12,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
          transition: "opacity 220ms ease, transform 220ms ease",
        }}
      >
        <span
          style={{
            fontSize: 14,
            fontWeight: 600,
            letterSpacing: "-0.005em",
            textShadow: "0 1px 2px rgba(0,0,0,0.6)",
            textTransform: "capitalize",
          }}
        >
          {name}
        </span>
        <span
          style={{
            fontSize: 11,
            fontWeight: 500,
            padding: "2px 8px",
            borderRadius: 999,
            background: "rgba(0,0,0,0.55)",
            color: "rgba(255,255,255,0.85)",
            backdropFilter: "blur(4px)",
            border: "1px solid rgba(255,255,255,0.08)",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {count.toLocaleString()}
        </span>
      </div>
      <div
        className="tn-cat-card__cta"
        style={{
          position: "absolute",
          left: 12,
          right: 12,
          bottom: 12,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
          opacity: 0,
          transform: "translateY(8px)",
          transition: "opacity 220ms ease, transform 220ms ease",
          pointerEvents: "none",
        }}
      >
        <span
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: "#fff",
            textShadow: "0 1px 2px rgba(0,0,0,0.6)",
            textTransform: "capitalize",
          }}
        >
          View {count.toLocaleString()} {name}
        </span>
        <ArrowRightOutlined style={{ color: "#fff", fontSize: 14 }} />
      </div>
    </Link>
  );
}
